import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router, NavigationEnd, ActivatedRoute, Event } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { toggleAnimation } from '../../../../shared/animations';
import { NotificationDataService, DropdownNotification } from '../../../../core/services/notification-service/notification-data.service';
import { SocketService } from '../../../../core/services/socket-service/socket.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  animations: [toggleAnimation]
})
export class HeaderComponent implements OnInit, OnDestroy {
  store: any;
  search = false;
  isLoading = false;
  
  pageTitle: string = '';
  
  private titleLoadedFromRoute = false;
  private routerSubscription: Subscription | null = null;
  private notificationSubscription: Subscription | null = null;
  private unreadCountSubscription: Subscription | null = null;
  private connectionSubscription: Subscription | null = null;
  
  // Combined notifications from API + Socket
  notifications: DropdownNotification[] = [];
  unreadCount = 0;
  isNotificationLoading = false;

  // Environment badge
  envBadge = environment.badge_env;

  constructor(
    public translate: TranslateService,
    public storeData: Store<any>,
    public router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private notificationDataService: NotificationDataService,
    private socketService: SocketService
  ) {
    this.initStore();

    this.pageTitle = 'Bienvenue dans votre espace Adhérent';
    this.titleService.setTitle(`LA MAS - ${this.pageTitle}`);
  }

  ngOnInit() {
    this.setActiveDropdown();
    this.setupTitleManagement();
    this.initializeNotifications();
  }

  private initializeNotifications(): void {
    // Request notification permission
    this.socketService.requestNotificationPermission();
    
    // Connect to socket for real-time updates
    this.socketService.connect();
    
    // Subscribe to combined notifications from service
    this.notificationSubscription = this.notificationDataService.dropdownNotifications$.subscribe(
      (notifications: DropdownNotification[]) => {
        this.notifications = notifications;
      }
    );
    
    // Subscribe to unread count
    this.unreadCountSubscription = this.notificationDataService.unreadCount$.subscribe(
      (count: number) => {
        this.unreadCount = count;
      }
    );

    // Subscribe to loading state
    this.notificationDataService.isLoading$.subscribe(
      (isLoading: boolean) => {
        this.isNotificationLoading = isLoading;
      }
    );

    // Subscribe to socket connection status
    this.connectionSubscription = this.socketService.connectionStatus$.subscribe(
      (isConnected: boolean) => {
        // Connection status handled by notification service
      }
    );
    
    // Load initial notifications from API
    this.loadInitialNotifications();
  }

  private loadInitialNotifications(): void {
    // Load notifications from API (includes missed notifications while offline)
    this.notificationDataService.loadDropdownNotifications();
  }

  private setupTitleManagement() {
    this.updateTitleFromCurrentRoute();
    
    this.routerSubscription = this.router.events.pipe(
      filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateTitleFromCurrentRoute();
      this.setActiveDropdown();
    });
  }
  
  private updateTitleFromCurrentRoute() {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    
    if (route.snapshot.data && route.snapshot.data['title']) {
      if (typeof route.snapshot.data['title'] === 'function') {
        this.pageTitle = route.snapshot.data['title'](route.snapshot);
      } else {
        this.pageTitle = route.snapshot.data['title'];
      }
      this.titleService.setTitle(`LA MAS - ${this.pageTitle}`);
      this.titleLoadedFromRoute = true;
    } else if (!this.titleLoadedFromRoute) {
      this.pageTitle = 'Bienvenue dans votre espace Adhérent';
      this.titleService.setTitle(`LA MAS - ${this.pageTitle}`);
    }
  }

  async initStore() {
    this.storeData
      .select((d) => d.index)
      .subscribe((d) => {
        this.store = d;
      });
  }

  setActiveDropdown() {
    const selector = document.querySelector('ul.horizontal-menu a[routerLink="' + window.location.pathname + '"]');
    if (selector) {
      selector.classList.add('active');
      const all: any = document.querySelectorAll('ul.horizontal-menu .nav-link.active');
      for (let i = 0; i < all.length; i++) {
        all[0]?.classList.remove('active');
      }
      const ul: any = selector.closest('ul.sub-menu');
      if (ul) {
        let ele: any = ul.closest('li.menu').querySelectorAll('.nav-link');
        if (ele) {
          ele = ele[0];
          setTimeout(() => {
            ele?.classList.add('active');
          });
        }
      }
    }
  }

 removeNotification(notificationId: string) {
    
    // Mark as read via notification service (handles both API and socket)
    this.notificationDataService.markAsRead(notificationId).subscribe({
      next: (response) => {
        if (!response.success) {
          console.error('❌ Failed to mark notification as read:', response);
        }
      },
      error: (error) => {
        console.error('💥 Error marking notification as read:', error);
      }
    });
  }

  // 🔔 NEW: Open notification (clicks on notification item)
openNotification(notification: DropdownNotification) {
    
    // Prevent multiple clicks
    if (this.isNotificationLoading) {
      return;
    }
    
    this.isNotificationLoading = true;
    
    this.notificationDataService.openNotification(notification.id_notification).subscribe({
      next: (response) => {
        this.isNotificationLoading = false;
        
        if (response.success && response.data) {
          this.handleNotificationNavigation(notification, response.data);
        } else {
          console.error('❌ API response unsuccessful:', response);
        }
      },
      error: (error) => {
        console.error('💥 API Error:', error);
        this.isNotificationLoading = false;
        this.handleNotificationNavigation(notification, notification);
      }
    });
  }

  markAllAsRead() {
    this.notificationDataService.markAllAsRead().subscribe({
      next: (response) => {
        // All notifications marked as read successfully
      },
      error: (error) => {
        console.error('Error marking all notifications as read:', error);
      }
    });
  }

  // Refresh notifications manually
  refreshNotifications(): void {
    this.notificationDataService.loadDropdownNotifications();
  }

  private handleNotificationNavigation(notification: DropdownNotification, notificationData: any) {
    
    // Use id_objet from API response, fallback to notification data
    const objectId = notificationData.id_objet || notificationData.id_notification;
    // Navigate based on notification type and object
    switch (notification.code_type_objet) {
      case 'DPEC':
        if (objectId) {
          this.router.navigate(['/pecs/fiche', objectId]).then(success => {
          }).catch(err => {
            console.error('❌ PEC Navigation error:', err);
          });
        } else {
          this.router.navigate(['/pecs/liste']);
        }
        break;
        
      case 'DALD':
        if (objectId) {
          this.router.navigate(['/afld/fiche/demande', objectId]).then(success => {
          }).catch(err => {
            console.error('❌ ALD Navigation error:', err);
          });
        } else {
          this.router.navigate(['/afld/liste']);
        }
        break;
        
      case 'DFDS':
        if (objectId) {
          this.router.navigate(['/fds/fiche/demande', objectId]).then(success => {
          }).catch(err => {
            console.error('❌ FDS Navigation error:', err);
          });
        } else {
          this.router.navigate(['/fds/liste']);
        }
        break;
        
      case 'DADH':
        if (objectId) {
          this.router.navigate(['/ma-famille', objectId]).then(success => {
          }).catch(err => {
            console.error('❌ Adhesion Navigation error:', err);
          });
        } else {
          this.router.navigate(['/ma-famille']);
        }
        break;
        
      case 'DREC':
        if (objectId) {
          this.router.navigate(['/reclamations/fiche', objectId]).then(success => {
          }).catch(err => {
            console.error('❌ Reclamation Navigation error:', err);
          });
        } else {
          this.router.navigate(['/reclamations/liste']);
        }
        break;
        
      case 'DEP':
        if (objectId) {
          this.router.navigate(['/ep/fiche', objectId]).then(success => {
          }).catch(err => {
            console.error('❌ EP Navigation error:', err);
          });
        } else {
          this.router.navigate(['/ep/liste']);
        }
        break;
        
      default:
        this.router.navigate(['/dashboard']);
        break;
    }
    
  }

  // Helper method to format date
  formatNotificationDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'À l\'instant';
    if (minutes < 60) return `il y a ${minutes} min`;
    if (hours < 24) return `il y a ${hours}h`;
    if (days < 7) return `il y a ${days} jour${days > 1 ? 's' : ''}`;
    
    return date.toLocaleDateString('fr-FR');
  }

  // Check if notification is unread
  isNotificationUnread(notification: DropdownNotification): boolean {
    return notification.flag_lu === 'N';
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
    if (this.unreadCountSubscription) {
      this.unreadCountSubscription.unsubscribe();
    }
    if (this.connectionSubscription) {
      this.connectionSubscription.unsubscribe();
    }
    
    // Disconnect socket
    this.socketService.disconnect();
  }
}